import React, {
  ReactElement,
  useReducer,
  Dispatch,
  useEffect,
  useState,
} from "react";
import SelectAccount from "./SelectAccount/SelectAccount";
import { EquityTradeInputs, EquityTradePreview } from "$gql/types.gen";
import { ClientUser, useFetchUser } from "source/hooks";
import RetreiveOauthUrl from "./RetreiveOauthUrl/RetreiveOauthUrl";
import TradeStock from "./TradeStock/TradeStock";
import CheckAccountStatus from "./CheckAccountStatus/CheckAccountStatus";
import ExternalBrokerError from "./ExternalBrokerError/ExternalBrokerError";
import TradePreview from "./TradePreview/TradePreview";
import TradeConfirmation from "./TradeConfirmation/TradeConfirmation";
import { useRouter } from "next/router";
import TermsAcceptance from "./TermsAcceptance/TermsAcceptance";
import SignedOut from "./SignedOut/SignedOut";
import LoadingTradeModal from "./Loading/LoadingTradeModal";
import { urlWithQueryString } from "@tiicker/util/lib/utils";
import Modal from "components/Modal/Modal";
import styles from "./TradeModal.module.scss";

interface Props {
  isShowing: boolean;
  setIsShowing(newValue: boolean): void;
  tickerSymbol: string;
  iexSymbol?: string;
  companyName: string;
  preSelectedAccountId?: number;
  isNative?: boolean;
  baseUrl: string;
}

export interface StepProps {
  state: TradeState;
  dispatch: Dispatch<Action>;
  setTitle(title: string): void;
  closeModal(): void;
}

type Action =
  | {
      type: "finishedLoading";
      payload: {
        user: ClientUser | null;
      };
    }
  | { type: "signedOut" }
  | { type: "acceptedTerms" }
  | {
      type: "setModalTitle";
      payload: { title: string };
    }
  | {
      type: "selectedAccount";
      payload: {
        brokerName: string;
        institutionLoginId: number;
        institutionAccountId: number;
        buyingPower: number;
        availableShares: number;
        accountNumber: string;
      };
    }
  | {
      type: "authenticatedAccount";
    }
  | { type: "oAuthExpired" }
  | { type: "needsOAuth" }
  | { type: "reset"; payload: TradeState }
  | { type: "needsFurtherAction" }
  | {
      type: "previewTrade";
      payload: { preview: EquityTradePreview; order: EquityTradeInputs };
    }
  | {
      type: "editOrder";
    }
  | {
      type: "executedTrade";
      payload: { orderNumber: number };
    }
  | {
      type: "setTradeType";
      payload: { tradeType: "buy" | "sell" };
    }
  | {
      type: "errorEncountered";
      payload: {
        errorMessage: string;
      };
    };

export interface TradeState {
  title: string;
  currentComponent(state: StepProps): React.ReactElement;
  currentUser: ClientUser | null;
  currentUserId: number;
  brokerName: string | null;
  oAuthVerifier: string | null;
  institutionAccountId: number | null;
  institutionLoginId: number | null;
  buyingPower: number | null;
  availableShares: number | null;
  accountNumber: string | null;
  tickerSymbol: string;
  iexSymbol?: string;
  companyName: string;

  currentTradeType: "buy" | "sell";

  tradePreview: EquityTradePreview | null;
  clientOrderId: string;
  order: EquityTradeInputs | null;

  currentQuestion: string | null;
  currentQuestionOptions: string[] | null;
  orderNumber: number | null;
  errorMessage: string | null;

  isNative: boolean;
  baseUrl: string;
}

const reducer = (state: TradeState, action: Action): TradeState => {
  switch (action.type) {
    case "finishedLoading":
      return {
        ...state,
        currentComponent:
          action.payload.user === null ? SignedOut : TermsAcceptance,
      };
    case "signedOut":
      return {
        ...state,
        currentComponent: SignedOut,
      };
    case "acceptedTerms":
      return {
        ...state,
        currentComponent: SelectAccount,
      };
    case "selectedAccount":
      return {
        ...state,
        brokerName: action.payload.brokerName,
        institutionAccountId: action.payload.institutionAccountId,
        institutionLoginId: action.payload.institutionLoginId,
        buyingPower: action.payload.buyingPower,
        accountNumber: action.payload.accountNumber,
        availableShares: action.payload.availableShares,
        currentComponent: CheckAccountStatus,
      };

    case "authenticatedAccount":
    case "editOrder":
      return {
        ...state,
        currentComponent: TradeStock,
      };

    case "needsOAuth":
    case "oAuthExpired":
      return {
        ...state,
        currentComponent: RetreiveOauthUrl,
      };

    case "needsFurtherAction":
      return {
        ...state,
        currentComponent: ExternalBrokerError,
      };

    case "setTradeType":
      return {
        ...state,
        currentTradeType: action.payload.tradeType,
      };

    case "previewTrade":
      return {
        ...state,
        currentComponent: TradePreview,
        tradePreview: action.payload.preview,
        order: action.payload.order,
        errorMessage: null,
      };

    case "executedTrade":
      return {
        ...state,
        currentComponent: TradeConfirmation,
        orderNumber: action.payload.orderNumber,
      };

    case "setModalTitle":
      return {
        ...state,
        title: action.payload.title,
      };

    case "reset":
      return {
        ...action.payload,
      };

    case "errorEncountered":
      return {
        ...state,
        errorMessage: action.payload.errorMessage,
      };
  }
};

export default function TradeModal({
  isShowing,
  setIsShowing,
  tickerSymbol,
  iexSymbol,
  companyName,
  preSelectedAccountId,
  isNative,
  baseUrl,
}: Props): ReactElement {
  const currentUser = useFetchUser();

  const [hasLoaded, setHasLoaded] = useState(false);
  const startingComponent =
    currentUser.user === undefined
      ? LoadingTradeModal
      : currentUser.user !== null
      ? TermsAcceptance
      : SignedOut;

  const initialState: TradeState = {
    title: "",
    currentComponent: preSelectedAccountId ? SelectAccount : startingComponent,
    currentUser: currentUser.user!,

    // This is so we don't have to check each time we access it
    // If user is not present, they will only be able to access the above
    // 'SignedOut' screen
    currentUserId: currentUser.user ? currentUser.user.id : -1,

    currentTradeType: "buy",

    tickerSymbol,
    iexSymbol,
    companyName,
    brokerName: null,
    oAuthVerifier: null,
    institutionAccountId: preSelectedAccountId || null,
    buyingPower: null,
    availableShares: null,
    accountNumber: null,
    institutionLoginId: null,
    currentQuestion: null,
    currentQuestionOptions: null,
    tradePreview: null,
    order: null,
    orderNumber: null,
    clientOrderId:
      Math.random().toString(36).substring(2, 10) +
      Math.random().toString(36).substring(2, 10),
    errorMessage: null,
    isNative: isNative || false,
    baseUrl,
  };

  // This is utilized to remove query params from the url,
  // the institutionAccountId is used to auto-popup a modal
  // state, if the user closes the modal, we want it to reset
  const router = useRouter();
  useEffect(() => {
    const { tab, symbol, institutionAccountId, ...remaining } = router.query;

    if (institutionAccountId) {
      const pathSplit = router.asPath.split("?");
      const href = pathSplit[0];

      const newUrl = urlWithQueryString(href, remaining);

      if (typeof window !== "undefined") {
        window.history.replaceState({}, document.title, newUrl);
      }
    }
  }, []);

  const [state, dispatch] = useReducer(reducer, initialState);

  // Keep track of user loading state and trigger the finish loading
  // action once this completes -- This is required due to our state
  // keeping track of the current component
  useEffect(() => {
    if (!hasLoaded && !currentUser.loading && currentUser.user !== undefined) {
      setHasLoaded(true);
      dispatch({
        type: "finishedLoading",
        payload: { user: currentUser.user },
      });
    }
  }, [currentUser.loading]);

  const cancelModal = React.useCallback(() => {
    dispatch({
      type: "reset",
      payload: { ...initialState, institutionAccountId: null },
    });
    setIsShowing(false);
  }, [dispatch, setIsShowing, initialState]);

  const setTitle = React.useCallback(
    (title: string) => {
      React.useEffect(() => {
        dispatch({ type: "setModalTitle", payload: { title } });
      }, [dispatch, title]);
    },
    [dispatch]
  );

  return (
    <Modal visible={isShowing} onClose={cancelModal} width={450}>
      <div className={styles.modalPadding}>
        <h5>{state.title}</h5>
        <state.currentComponent
          state={state}
          dispatch={dispatch}
          setTitle={setTitle}
          closeModal={cancelModal}
        />
      </div>
    </Modal>
  );
}
